/**
 * Chart tooltip — one tooltip per chart, shown on hover AND on keyboard focus of a mark
 * (spec §6: "אותו מידע במיקוד מקלדת"). Content is structured, never raw HTML.
 */

import { useCallback, useState } from 'react';
import type { FocusEvent, PointerEvent as ReactPointerEvent } from 'react';

export interface TipRow {
  /** Series swatch colour, e.g. `var(--series-1)`. */
  color?: string;
  /** Bold value. */
  value?: string;
  /** Trailing label. */
  label?: string;
  muted?: boolean;
}

export interface TipContent {
  title: string;
  rows: TipRow[];
}

export interface TooltipApi {
  /** Bind to a mark: shows the tooltip at the pointer or at the element's box on focus. */
  bind: (content: TipContent) => {
    onPointerEnter: (event: ReactPointerEvent) => void;
    onPointerMove: (event: ReactPointerEvent) => void;
    onPointerLeave: () => void;
    onFocus: (event: FocusEvent) => void;
    onBlur: () => void;
  };
  node: TipState | null;
  hide: () => void;
}

export interface TipState {
  content: TipContent;
  x: number;
  y: number;
}

export function useTooltip(): TooltipApi {
  const [state, setState] = useState<TipState | null>(null);
  const hide = useCallback(() => setState(null), []);

  const bind = useCallback(
    (content: TipContent) => ({
      onPointerEnter: (event: ReactPointerEvent) => {
        if (event.pointerType === 'touch') return;
        setState({ content, x: event.clientX, y: event.clientY });
      },
      onPointerMove: (event: ReactPointerEvent) => {
        if (event.pointerType === 'touch') return;
        setState({ content, x: event.clientX, y: event.clientY });
      },
      onPointerLeave: hide,
      onFocus: (event: FocusEvent) => {
        const rect = (event.target as Element).getBoundingClientRect();
        setState({ content, x: rect.left + rect.width / 2, y: rect.top });
      },
      onBlur: hide,
    }),
    [hide],
  );

  return { bind, node: state, hide };
}

export function ChartTooltip({ state }: { state: TipState | null }) {
  if (!state) return null;
  const width = 260;
  const left = Math.max(8, Math.min(state.x + 14, (typeof window === 'undefined' ? 1024 : window.innerWidth) - width - 8));
  const top = Math.max(8, state.y + 14);
  return (
    <div className="tooltip show" role="tooltip" style={{ left: `${left}px`, top: `${top}px` }}>
      <div className="tt-h">{state.content.title}</div>
      {state.content.rows.map((row, index) => (
        <div key={index} className={`tt-r${row.muted ? ' meta' : ''}`}>
          {row.color ? <i style={{ background: row.color }} /> : null}
          {row.value ? <b>{row.value}</b> : null}
          {row.label ? <span>{row.label}</span> : null}
        </div>
      ))}
    </div>
  );
}
